import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import {
  Box,
  Chip,
  Stack,
  Button,
  Dialog,
  Divider,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';

import { toPreLineHtml } from 'src/utils/text/html-text';
import { extractHashtags } from 'src/utils/text/hashtag-extract';
import { getPlatformInfo } from 'src/utils/platform/platform-info';
import { highlightTextWithHtmlRemoval } from 'src/utils/text/text-highlight';
import { formatNumber, formatTimestamp } from 'src/utils/format/format-utils';
import { getMetricsByScoreTime } from 'src/utils/metrics/post-metric-snapshot';
import { readNegativeEvidence } from 'src/utils/moments/view/negative-evidence';
import { isPostVideo, pickPostImages, readVideoMetas, formatVideoDuration } from 'src/utils/moments/view/post-media';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';
import { CountryTag } from 'src/components/cmui/country-tag';
import SafeHtml from 'src/components/cmui/post-card/SafeHtml';
import { PostCardBookmarkOverlay } from 'src/components/cmui/bookmark';
import { PostCardCampaignOverlay } from 'src/components/cmui/campaign';
import PostKindChips from 'src/components/cmui/post-card/PostKindChips';
import PostMediaBadge from 'src/components/cmui/post-card/PostMediaBadge';
import HighlightedText from 'src/components/cmui/post-card/HighlightedText';
import PostMetricTrend from 'src/components/cmui/post-card/PostMetricTrend';
import PostTranslateBlock from 'src/components/cmui/post-card/PostTranslateBlock';
import { Carousel, useCarousel, CarouselDotButtons, CarouselArrowNumberButtons } from 'src/components/carousel';

import { NegativeMentionList } from './negative-mention-list';



/**
 * 게시물 상세 — 화면 가운데 다이얼로그.
 * ⚠️ 게시물 탭(`moment-content`)과 소재 목록은 이것 대신 소재 상세(`MediaDetailDialog`)를 연다.
 *
 * ⚠️ 구조분해 파라미터는 `props.<이름>` 형태여야 한다 — 루트 `@param`이 없으면 TS가
 * **첫 `@param`의 타입을 props 전체로** 읽어 `.tsx` 호출부가 아무 prop도 못 넘긴다.
 *
 * @param {Object} props
 * @param {Object|null} [props.post] - 게시물 데이터 (PostCard와 동일한 구조)
 * @param {boolean} [props.open] - 열림 상태
 * @param {Function} [props.onClose] - 닫기 핸들러
 * @param {string} [props.searchKeyword]
 * @param {string} [props.clientSearch]
 * @param {string[]} [props.topicKeywords]
 * @param {boolean} [props.isBookmark=false] - true면 북마크 오버레이(칩) 숨김 (폴더 다중 선택 모드 등)
 * @param {boolean} [props.enableCampaignAdd=false]
 * @param {boolean} [props.detailUsesMetricDetailSort=false] - true면 커스텀 서치 등 metricsSort 기준 표시
 */
function PostDetail({
  post,
  open,
  onClose,
  searchKeyword = '',
  clientSearch = '',
  topicKeywords = [],
  isBookmark = false,
  enableCampaignAdd = false,
  detailUsesMetricDetailSort = false,
}: {
  post?: any
  open?: boolean
  onClose?: () => void
  searchKeyword?: string | readonly string[]
  clientSearch?: string
  topicKeywords?: readonly string[]
  isBookmark?: boolean
  enableCampaignAdd?: boolean
  detailUsesMetricDetailSort?: boolean
}) {
  const [imageError, setImageError] = useState(false);
  const formatTimestampFn = formatTimestamp;

  // 닫히면 이미지 에러를 지운다 — 다음에 열 때 다시 그려 본다
  useEffect(() => {
    if (!open) setImageError(false);
  }, [open]);

  // post가 변경되면 이미지 에러 초기화
  useEffect(() => {
    setImageError(false);
  }, [post?.id, post?.imageUrl, post?.imageS3Url]);

  // 모바일 스와이프 제스처로 뒤로가기 대신 이 창만 닫히도록 처리
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const lastXRef = useRef(0);
  const isHorizontalSwipeRef = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches?.[0];
    if (!t) return;
    startXRef.current = t.clientX;
    startYRef.current = t.clientY;
    lastXRef.current = t.clientX;
    isHorizontalSwipeRef.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const t = e.touches?.[0];
    if (!t) return;
    const dx = t.clientX - startXRef.current;
    const dy = t.clientY - startYRef.current;
    lastXRef.current = t.clientX;
    // 수평 이동이 우세하고 임계값 초과 시 기본 동작 차단 (브라우저 뒤로가기 제스처 방지)
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 12) {
      isHorizontalSwipeRef.current = true;
      try { e.preventDefault(); } catch { /* no-op */ }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    const totalDx = lastXRef.current - startXRef.current;
    if (isHorizontalSwipeRef.current && Math.abs(totalDx) > 28) {
      try { onClose?.(); } catch { /* no-op */ }
    }
    isHorizontalSwipeRef.current = false;
  }, [onClose]);

  // 시간 표시: timestamp 우선, 없으면 date 사용
  const timeDisplay = React.useMemo(() => {
    if (post?.timestamp) return formatTimestampFn(post.timestamp);
    if (post?.date) return formatTimestampFn(post.date);
    return '';
  }, [post?.timestamp, post?.date, formatTimestampFn]);

  if (!post) return null;

  const {
    title,
    content,
    author,
    account,
    timestamp,
    date,
    canonicalBrands,
    canonicalIngredients,
    canonicalConcerns,
    brands = [],
    link,
    // persona,
    market,
    sentimentEvidencePositive = [],
    originPost,
    repostAuthor,
    repostAccount,
  } = post;
  const sentimentEvidenceNegative = readNegativeEvidence(post);

  // isComment는 post에서 가져옴
  const isCommentValue = Boolean(post?.isComment);
  const isStoryValue = Boolean(post?.isStory);

  // canonicalBrand/canonicalIngredients/canonicalConcerns 필드를 그대로 사용
  const displayBrands = canonicalBrands !== undefined
    ? (Array.isArray(canonicalBrands) ? canonicalBrands : [])
    : (Array.isArray(brands) ? brands : []);
  const displayIngredients = Array.isArray(canonicalIngredients) ? canonicalIngredients : [];
  const displayConcerns = Array.isArray(canonicalConcerns) ? canonicalConcerns : [];

  // 개행 기준: <br>/<p> 등만 개행으로 바꾼다 — 세척은 렌더 경계의 `SafeHtml` 이 한다
  const formatForPreLine = toPreLineHtml;

  const latestMetrics = (() => {
    const metricsByScore = getMetricsByScoreTime(post, {
      useMetricDetailSort: detailUsesMetricDetailSort,
    });
    return {
      ...metricsByScore,
      metricScoreTime: metricsByScore?.requestDateTime,
    };
  })();
  // 여러 장이면 캐러셀, 한 장이면 그대로 — 판단은 pickPostImages 가 한다
  const mediaImages = pickPostImages(post);
  const videoMetas = readVideoMetas(post);
  const isVideoPost = isPostVideo(post);

  // Engagement Rate 계산: (좋아요+댓글+저장+다운로드+북마크)/조회
  const calculateEngagementRate = () => {
    const like = latestMetrics.likeCount || 0;
    const comment = latestMetrics.commentCount || 0;
    const saved = latestMetrics.savedCount || 0;
    const download = latestMetrics.downloadCount || 0;
    const share = latestMetrics.shareCount || 0;
    const view = latestMetrics.viewCount || 0;

    // 조회가 0 이하인 경우 "-" 반환
    if (view <= 0) {
      return '-';
    }

    // 북마크는 saved로 간주
    const totalEngagement = like + comment + saved + download + share;
    const rate = (totalEngagement / view) * 100;

    // 소수점 둘째 자리까지 표시
    return `${rate.toFixed(2)}%`;
  };

  const engagementRate = calculateEngagementRate();

  // 해시태그 추출
  const titleHashtags = extractHashtags(title);
  const contentHashtags = extractHashtags(content);
  const allHashtags = [...new Set([...titleHashtags, ...contentHashtags])];

  // 텍스트 하이라이트: formatForPreLine으로 정리한 뒤 공용 유틸 사용 (순수 텍스트에서 키워드만 하이라이트)
  const highlightText = (text: unknown) =>
    highlightTextWithHtmlRemoval(
      formatForPreLine(text),
      searchKeyword as string,
      topicKeywords as string[],
      clientSearch,
      sentimentEvidencePositive,
      sentimentEvidenceNegative
    );

  /**
   * 🔑 신원(계정명·작성자명)은 **마크업 없이** 그린다 — `dangerouslySetInnerHTML` 을 쓰지 않는다.
   * 그 값에 태그가 필요한 이유가 없는데 HTML 통로를 열어 두면 «글쓴이가 고른 문자열»이
   * 태그가 된다. React 가 텍스트를 늘 escape 하므로 여기서는 태그가 생길 수 없다.
   */
  const renderIdentity = (text: string | null | undefined, prefix = '') => (
    <HighlightedText
      text={text}
      prefix={prefix}
      searchKeyword={searchKeyword as string}
      topicKeywords={topicKeywords as string[]}
      clientSearch={clientSearch}
      sentimentPositive={sentimentEvidencePositive}
      sentimentNegative={sentimentEvidenceNegative}
    />
  );

  /*
   * 🚨 번역문은 **텍스트 노드로 그린다** — `dangerouslySetInnerHTML` 을 쓰지 않는다.
   *
   * 글 본문은 외부인이 쓰는 소셜 텍스트라 «프롬프트 주입 → 모델이 태그 출력 → DOM 주입»이
   * 성립한다. 방어는 두 겹이다: ① `lib/ai` 경계가 평문을 보장하고(기본 세척)
   * ② 화면이 텍스트로 그린다.
   * ⚠️ 여기에 다시 `dangerouslySetInnerHTML` 을 쓰지 마라 — 그러면 ②가 사라진다.
   */

  return (
    <Dialog
      open={open === true}
      onClose={onClose}
      keepMounted
      fullWidth
      maxWidth={false}
      // 전체 화면 위에 뜨는 창이다 — 사이드바·툴바보다 위여야 한다
      sx={{ zIndex: 10000 }}
      PaperProps={{
        sx: {
          borderRadius: RADIUS.lg,
          width: { xs: 'calc(100vw - 32px)', sm: 560 },
          maxWidth: 560,
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100vh - 32px)', sm: 'calc(100vh - 64px)' },
          bgcolor: 'white',
          // 수평 스와이프 시 브라우저 제스처 억제
          touchAction: 'pan-y',
          overscrollBehaviorX: 'contain',
        },
      }}
    >
      <Box
        // 다이얼로그 Paper 는 flex column 이다 — 남는 높이를 받아 자기 안에서만 구른다
        sx={{ p: 3.75, flex: 1, minHeight: 0, overflowY: 'auto', position: 'relative', bgcolor: 'white' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* 상단 헤더: 플랫폼 아이콘(좌) + 작성자/계정+국기(우), 닫기 버튼은 우측 상단 */}
        <Box sx={{ position: 'relative', mb: 3, width: '100%' }}>
          {/* 우측 상단: 북마크(PostCard와 동일) + 닫기 버튼 */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ position: 'absolute', top: -8, right: -8, zIndex: 1 }}>
            {!isBookmark && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box onClick={(e) => e.stopPropagation()}>
                  <PostCardBookmarkOverlay
                    placement="postCard"
                    post={post}
                  />
                </Box>
                {enableCampaignAdd && (
                  <Box onClick={(e) => e.stopPropagation()}>
                    <PostCardCampaignOverlay
                      placement="postCard"
                      post={post}
                      showLabel
                    />
                  </Box>
                )}
              </Stack>
            )}
            <IconButton aria-label="닫기" onClick={onClose} sx={{ width: 34, height: 34, color: 'text.secondary' }}>
              <Iconify icon="mingcute:close-line" width={20} />
            </IconButton>
          </Stack>

          {(() => {
            const isQuote = Boolean(post?.isQuote)
            const isRepost = Boolean(post?.isRepost)
            const isStory = isStoryValue
            const isRepostClone = isRepost && Boolean(repostAuthor || repostAccount || post?.repostOriginalPost)

            const displayAuthor = isRepostClone ? (repostAuthor || author || '') : (author || '')
            const displayAccount = isRepostClone
              ? (repostAccount || account || post?.username || '')
              : (account || post?.username || '')

            return (
              <>
                {/* 유형·광고 칩 — 칩 넷의 라벨·색·크기가 PostKindChips 의 `detail` 변형과
                    글자까지 같아, 여기 있던 사본을 그 컴포넌트로 합쳤다(틱톡 광고 태그도 함께 온다) */}
                <PostKindChips
                  post={post}
                  flags={{ isComment: isCommentValue, isQuote, isRepost, isStory }}
                  variant="detail"
                  stackSx={{ mb: 1.5 }}
                />

                {/* 공통 헤더(채널/계정/아이디) */}
                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                  {post.source && (
                    <Box
                      component="img"
                      src={`/assets/icons/platforms/${String(post.source).toLowerCase()}.svg`}
                      alt={getPlatformInfo(post.source)?.label || post.source}
                      sx={{ width: 50, height: 50, flexShrink: 0 }}
                    />
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                    {displayAuthor && (
                      <Typography
                        sx={{ typography: 'uiTitle', fontWeight: 600, color: 'text.primary', lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word' }}
                      >
                        {renderIdentity(`${displayAuthor || ''}`)}
                      </Typography>
                    )}

                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
                      {displayAccount && (
                        <Typography
                          sx={{ typography: 'uiTitle', fontWeight: 'light', color: 'text.secondary', whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                          {renderIdentity(`@${displayAccount}`)}
                        </Typography>
                      )}
                      {market && (
                        <Typography component="div" sx={{ typography: 'uiTitle', fontWeight: 'light', color: 'text.secondary', whiteSpace: 'normal', overflow: 'visible', ml: 0.5 }}>
                          <CountryTag code={market} size={18} />
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </>
            )
          })()}
        </Box>

        {/* 헤더 바로 아래: 이미지. 여러 장이면 캐러셀 (에러 시 전체 섹션 숨김) */}
        {(mediaImages.length > 0 && !imageError) && (
          <Box sx={{ mb: 3.75 }}>
            <PostMediaImages
              post={post}
              images={mediaImages}
              videoCount={videoMetas.length}
              onError={() => setImageError(true)}
            />
          </Box>
        )}

        {(videoMetas.length > 0 || (isVideoPost && (mediaImages.length === 0 || imageError))) && (
          <Box sx={{ mb: 3.75 }}>
            <PostVideoMeta
              // 딱지는 이미지 위가 제자리다 — 그림이 없을 때만 여기로 내려온다
              post={post}
              showBadge={isVideoPost && (mediaImages.length === 0 || imageError)}
              metas={videoMetas}
            />
          </Box>
        )}

        {/* 이미지 아래: 제목/내용 */}
        {(title || content) && (
          <Box sx={{ mb: 3.75 }}>
            {title && (
              <Typography
                variant="h6"
                sx={{ fontWeight: content ? 'bold' : 'Medium', color: 'text.primary', mb: content ? 1.5 : 0, whiteSpace: 'pre-line', wordBreak: 'break-all' }}
              >
                <SafeHtml html={highlightText(title)} />
              </Typography>
            )}
            {content && (
              <Typography
                variant="body1"
                sx={{ fontWeight: 'Medium', lineHeight: 1.6, whiteSpace: 'pre-line', wordBreak: 'break-all' }}
              >
                <SafeHtml html={highlightText(content)} />
              </Typography>
            )}

            <NegativeMentionList post={post} sx={{ mt: 2 }} />

            <PostTranslateBlock title={formatForPreLine(title)} body={formatForPreLine(content)} resetKey={String(post?.id ?? '')} sx={{ mt: 2 }} />

            {/* 해시태그: 컨텐츠 아래에 중립 색상으로 표시 */}
            {allHashtags.length > 0 && (
              <Box sx={{ mt: 3.75 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {allHashtags.map((hashtag, i) => (
                    <Chip
                      key={`hashtag-below-content-${hashtag}-${i}`}
                      label={hashtag}
                      size="small"
                      clickable={false}
                      tabIndex={-1}
                      component="div"
                      sx={{
                        mr: 0.5,
                        mb: 0.5,
                        minHeight: 26,
                        height: 26,
                        minWidth: 0,
                        borderRadius: RADIUS.pill,
                        bgcolor: 'white',
                        color: 'black',
                        border: '1px solid',
                        borderColor: 'divider',
                        pointerEvents: 'none',
                        cursor: 'default',
                        boxShadow: 'none',
                        '& .MuiChip-label': { px: 1, py: 0, lineHeight: 1 },
                        '&:hover, &:focus, &:active': {
                          bgcolor: 'white',
                          color: 'black',
                          borderColor: 'divider',
                          boxShadow: 'none',
                          outline: 'none',
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}


        {/* 원글 섹션 */}
        {originPost && typeof originPost === 'object' && Object.keys(originPost).length > 0 && (
          <>
            <Divider sx={{ my: 3.75, borderColor: 'divider' }} />
            {/* 인용/리포스트한 글 타이틀 */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              {post?.isRepost ? '리포스트한 글' : '인용한 글'}
            </Typography>
            <Box
              sx={{
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: RADIUS.lg,
                p: { xs: 2, sm: 2.5 },
              }}
            >


              {/* 원글 헤더: 플랫폼 아이콘 + 작성자/계정 */}
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                {originPost?.source && (
                  <Box
                    component="img"
                    src={`/assets/icons/platforms/${String(originPost.source).toLowerCase()}.svg`}
                    alt={getPlatformInfo(originPost.source)?.label || originPost.source}
                    sx={{ width: 50, height: 50, flexShrink: 0 }}
                  />
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  {originPost?.author && (
                    <Typography
                      sx={{ typography: 'uiTitle', fontWeight: 600, color: 'text.primary', lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word', mb: 0.5 }}
                    >
                      {renderIdentity(`${originPost.author || ''}`)}
                    </Typography>
                  )}
                  {originPost?.account && (
                    <Typography
                      sx={{ typography: 'uiTitle', fontWeight: 'light', color: 'text.secondary', whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                      {renderIdentity(`@${originPost.account}`)}
                    </Typography>
                  )}
                </Box>
              </Stack>

              {/* 원글 이미지 */}
              {originPost?.imageUrl && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ width: '100%', aspectRatio: '1 / 1', position: 'relative', borderRadius: "20px", overflow: 'hidden', bgcolor: '#f9fafb' }}>
                    <Box
                      component="img"
                      src={originPost.imageUrl}
                      alt="origin post media"
                      onError={(e) => ((e.target as HTMLElement).style.display = "none")}
                      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                </Box>
              )}

              {/* 원글 텍스트 */}
              {originPost?.text && (
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'Medium', lineHeight: 1.6, whiteSpace: 'pre-line', wordBreak: 'break-all', mb: 2 }}
                >
                  <SafeHtml html={highlightText(originPost.text)} />
                </Typography>
              )}

              {/* 원글 시간 정보 */}
              {(originPost?.timestamp || originPost?.date) && (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ mb: 2 }}
                >
                  {originPost?.timestamp
                    ? formatTimestampFn(originPost.timestamp)
                    : (originPost?.date ? formatTimestampFn(originPost.date) : '')}
                </Typography>
              )}

              {/* 원글 링크 버튼 */}
              {originPost?.link && (
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="solar:square-top-down-linear" />}
                  onClick={() => window.open(originPost.link, '_blank')}
                  sx={{
                    width: '100%',
                    borderRadius: RADIUS.pill,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      bgcolor: 'primary.lighter',
                    },
                  }}
                >
                  원글 확인하기
                </Button>
              )}
            </Box>
          </>
        )}

        <Divider sx={{ my: 3.75, borderColor: 'divider' }} />

        {isStoryValue && (
          <Box
            sx={{
              mb: 2,
              px: 1.5,
              py: 1.5,
              borderRadius: RADIUS.md,
              bgcolor: 'secondary.lighter',
              border: '1px solid',
              borderColor: 'secondary.light',
            }}
          >
            <Typography variant="body2" sx={{ color: 'secondary.dark', fontWeight: 600 }}>
              인스타그램 스토리에서 수집된 포스트입니다.
            </Typography>
          </Box>
        )}

        {!isStoryValue && (
          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Tooltip
                title="Engagement Rate = (좋아요+댓글+저장+다운로드+북마크)/조회"
                arrow
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'zIndex',
                        enabled: true,
                        phase: 'write',
                        fn: ({ state }) => {
                          state.styles.popper.zIndex = 14000 as unknown as string;
                        },
                      },
                    ],
                  },
                }}
              >
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                  Engagement Rate
                </Typography>
              </Tooltip>
              <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                {engagementRate}
              </Typography>
            </Stack>
          </Box>
        )}

        {!isStoryValue && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" flexWrap="wrap" sx={{ width: '100%' }}>
            {/* 좋아요 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: 26,
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: 1.5,
                py: 0.625,
              }}
            >
              <Box component="img" src="/assets/icons/icon_svg/like.svg" sx={{ width: 16, height: 16, filter: 'brightness(0) saturate(100%)' }} />
              <Typography variant="caption" sx={{ color: 'black', fontWeight: 'Medium', fontSize: '13.5px' }}>
                {formatNumber(latestMetrics.likeCount)}
              </Typography>
            </Box>
            {/* 댓글 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: 26,
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: 1.5,
                py: 0.625,
              }}
            >
              <Box component="img" src="/assets/icons/icon_svg/reply.svg" sx={{ width: 16, height: 16, filter: 'brightness(0) saturate(100%)' }} />
              <Typography variant="caption" sx={{ color: 'black', fontWeight: 'Medium', fontSize: '13.5px' }}>
                {formatNumber(latestMetrics.commentCount)}
              </Typography>
            </Box>
            {/* 조회수 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: 26,
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: 1.5,
                py: 0.625,
              }}
            >
              <Box component="img" src="/assets/icons/icon_svg/view.svg" sx={{ width: 16, height: 16, filter: 'brightness(0) saturate(100%)' }} />
              <Typography variant="caption" sx={{ color: 'black', fontWeight: 'Medium', fontSize: '13.5px' }}>
                {post?.contentsType === 'instagram_post' && latestMetrics.viewCount === 0
                  ? '-'
                  : formatNumber(latestMetrics.viewCount)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: 26,
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: 1.5,
                py: 0.625,
              }}
            >
              <Box component="img" src="/assets/icons/icon_svg/bookmark.svg" sx={{ width: 16, height: 16, filter: 'brightness(0) saturate(100%)' }} />
              <Typography variant="caption" sx={{ color: 'black', fontWeight: 'Medium', fontSize: '13.5px' }}>
                {formatNumber(latestMetrics.savedCount)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: 26,
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: 1.5,
                py: 0.625,
              }}
            >
              <Box component="img" src="/assets/icons/icon_svg/share.svg" sx={{ width: 16, height: 16, filter: 'brightness(0) saturate(100%)' }} />
              <Typography variant="caption" sx={{ color: 'black', fontWeight: 'Medium', fontSize: '13.5px' }}>
                {formatNumber(latestMetrics.shareCount)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: 26,
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: 1.5,
                py: 0.625,
              }}
            >
              <Box component="img" src="/assets/icons/icon_svg/download.svg" sx={{ width: 16, height: 16, filter: 'brightness(0) saturate(100%)' }} />
              <Typography variant="caption" sx={{ color: 'black', fontWeight: 'Medium', fontSize: '13.5px' }}>
                {formatNumber(latestMetrics.downloadCount)}
              </Typography>
            </Box>
          </Stack>
        )}

        {/* 작성자 정보 */}
        <Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
            mt={isStoryValue ? 0 : 1.5}
            sx={{ width: '100%' }}
          >
            {/* {persona && (
              <Typography variant="body2" color="text.secondary">
                페르소나: {persona}
              </Typography>
            )} */}
            {(date || timestamp) && (
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{ mt: { xs: 0.5, sm: 0 }, ml: { xs: 0, sm: 'auto' } }}
              >
                {timeDisplay}
                {latestMetrics.metricScoreTime
                  ? ` | ${formatTimestamp(latestMetrics.metricScoreTime)} 업데이트`
                  : ''}
              </Typography>
            )}
          </Stack>


          <Divider sx={{ my: 3.75 }} />




          {displayBrands.length > 0 && (
            <>
              {/* 브랜드와 해시태그 */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  언급 브랜드
                </Typography>
                <Box mb={2} sx={{ mt: 1.5 }}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {displayBrands.map((brand, i) => (
                      <Chip
                        key={`brand-${brand}-${i}`}
                        label={brand}
                        size="small"
                        sx={{
                          mr: 0.5,
                          mb: 0.5,
                          minHeight: 26,
                          height: 26,
                          minWidth: 0,
                          '& .MuiChip-label': { px: 1, py: 0, lineHeight: 1 },
                          fontWeight: 600,
                          bgcolor: 'primary.lighter',
                          color: 'primary.dark',
                          pointerEvents: 'none',
                          cursor: 'default',
                          borderRadius: RADIUS.pill,
                          '&:hover, &:focus, &:active': {
                            bgcolor: 'primary.lighter',
                            color: 'primary.dark',
                            boxShadow: 'none',
                            outline: 'none',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
              <Divider sx={{ my: 3.75 }} />
            </>
          )}

          {displayIngredients.length > 0 && (
            <>
              {/* 언급 성분 */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  언급 성분
                </Typography>
                <Box mb={2} sx={{ mt: 1.5 }}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {displayIngredients.map((ingredient, i) => (
                      <Chip
                        key={`ingredient-${ingredient}-${i}`}
                        label={ingredient}
                        size="small"
                        sx={{
                          mr: 0.5,
                          mb: 0.5,
                          minHeight: 26,
                          height: 26,
                          minWidth: 0,
                          '& .MuiChip-label': { px: 1, py: 0, lineHeight: 1 },
                          fontWeight: 600,
                          bgcolor: 'success.lighter',
                          color: 'success.dark',
                          pointerEvents: 'none',
                          cursor: 'default',
                          borderRadius: RADIUS.pill,
                          '&:hover, &:focus, &:active': {
                            bgcolor: 'success.lighter',
                            color: 'success.dark',
                            boxShadow: 'none',
                            outline: 'none',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
              <Divider sx={{ my: 3.75 }} />
            </>
          )}

          {displayConcerns.length > 0 && (
            <>
              {/* 언급 피부고민 */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  언급 피부고민
                </Typography>
                <Box mb={2} sx={{ mt: 1.5 }}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {displayConcerns.map((concern, i) => (
                      <Chip
                        key={`concern-${concern}-${i}`}
                        label={concern}
                        size="small"
                        sx={{
                          mr: 0.5,
                          mb: 0.5,
                          minHeight: 26,
                          height: 26,
                          minWidth: 0,
                          '& .MuiChip-label': { px: 1, py: 0, lineHeight: 1 },
                          fontWeight: 600,
                          bgcolor: 'warning.lighter',
                          color: 'warning.dark',
                          pointerEvents: 'none',
                          cursor: 'default',
                          borderRadius: RADIUS.pill,
                          '&:hover, &:focus, &:active': {
                            bgcolor: 'warning.lighter',
                            color: 'warning.dark',
                            boxShadow: 'none',
                            outline: 'none',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
              <Divider sx={{ my: 3.75 }} />
            </>
          )}



          {!isStoryValue && (
            <PostMetricTrend post={post} useMetricDetailSort={detailUsesMetricDetailSort} sx={{ mt: 2 }} />
          )}
        </Box>





        {/* 액션 버튼들 */}
        <Stack direction="row" spacing={1} sx={{ width: '100%', mt: 2 }}>
          {link && (
            <Button
              variant="contained"
              startIcon={<Iconify icon="solar:square-top-down-linear" />}
              onClick={() => window.open(link, '_blank')}
              sx={{
                width: '100%',
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: RADIUS.pill,
                mt: 3.75,
                '&:hover': { bgcolor: 'primary.main' },
              }}
            >
              포스트 확인하기
            </Button>
          )}
        </Stack>

      </Box>
    </Dialog>
  );
}

PostDetail.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    author: PropTypes.string,
    account: PropTypes.string,
    source: PropTypes.string,
    imageUrl: PropTypes.string,
    imageS3Url: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    indexedAt: PropTypes.string,
    brands: PropTypes.arrayOf(PropTypes.string),
    likeCount: PropTypes.number,
    commentCount: PropTypes.number,
    viewCount: PropTypes.number,
    canonicalIngredients: PropTypes.arrayOf(PropTypes.string),
    canonicalConcerns: PropTypes.arrayOf(PropTypes.string),
    persona_age: PropTypes.string,
    persona_gender: PropTypes.string,
    sentimentEvidencePositive: PropTypes.arrayOf(PropTypes.string),
    sentimentEvidenceNegative: PropTypes.arrayOf(PropTypes.string),
    metrics: PropTypes.objectOf(PropTypes.shape({
      likeCount: PropTypes.number,
      commentCount: PropTypes.number,
      viewCount: PropTypes.number,
      savedCount: PropTypes.number,
      shareCount: PropTypes.number,
      downloadCount: PropTypes.number,
      requestDateTime: PropTypes.string,
    })),
    link: PropTypes.string,
    sentiment: PropTypes.string,
    purchaseIntent: PropTypes.string,
    persona: PropTypes.string,
    market: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  searchKeyword: PropTypes.string,
  clientSearch: PropTypes.string,
  topicKeywords: PropTypes.arrayOf(PropTypes.string),
  isBookmark: PropTypes.bool,
  enableCampaignAdd: PropTypes.bool,
  detailUsesMetricDetailSort: PropTypes.bool,
};


/**
 * 글의 이미지. **두 장 이상이면 캐러셀**, 한 장이면 그대로 그린다.
 *
 * ⚠️ 한 장일 때 캐러셀을 쓰지 않는다 — 점·화살표가 붙어 「더 있다」고 잘못 말한다.
 * 로딩 실패는 섹션 통째로 숨기는 쪽(`onError`)으로 올려 보낸다.
 */
function PostMediaImages({
  post,
  images,
  videoCount,
  onError,
}: {
  post: unknown;
  images: string[];
  videoCount: number;
  onError: () => void;
}) {
  const carousel = useCarousel({ loop: images.length > 2 });

  const frame = (src: string, alt: string) => (
    <Box sx={{ width: '100%', position: 'relative', borderRadius: RADIUS.lg, overflow: 'hidden', bgcolor: '#f9fafb' }}>
      <Box
        component="img"
        src={src}
        alt={alt}
        onError={onError}
        sx={{ width: '100%', height: 'auto', display: 'block' }}
      />
    </Box>
  );

  // 딱지는 목록 카드와 같은 부품이다 — 자리는 부품이 스스로 잡는다.
  // 장수는 캐러셀의 「n/N」 이 이미 적으므로 숨긴다
  const badge = <PostMediaBadge post={post} hideImageCount videoCount={videoCount} />;

  if (images.length === 1) {
    return (
      <Box sx={{ position: 'relative' }}>
        {frame(images[0], 'post media')}
        {badge}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {badge}
      <Carousel carousel={carousel}>
        {images.map((src, index) => (
          <Box key={src} sx={{ width: '100%' }}>
            {frame(src, `post media ${index + 1}`)}
          </Box>
        ))}
      </Carousel>

      <CarouselArrowNumberButtons
        {...carousel.arrows}
        options={carousel.options}
        totalSlides={carousel.dots.dotCount}
        selectedIndex={carousel.dots.selectedIndex + 1}
        sx={{ position: 'absolute', right: 12, top: 12 }}
      />

      <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
        <CarouselDotButtons
          scrollSnaps={carousel.dots.scrollSnaps}
          selectedIndex={carousel.dots.selectedIndex}
          onClickDot={carousel.dots.onClickDot}
        />
      </Stack>
    </Box>
  );
}

/**
 * 영상 메타 — 값이 있는 칸만 적는다.
 *
 * ⚠️ `size` 는 단위가 색인에서 확인되지 않아 **숫자를 그대로** 적는다. 바이트로 단정해
 * 「MB」를 붙이면 틀린 값을 말하게 된다.
 * ⚠️ 딱지(`showBadge`)는 **그림이 없을 때만** 여기 붙는다 — 사진이 있으면 사진 위가 제자리다.
 * 영상인데 메타가 없는 글이 흔해서, 그때도 딱지는 남겨야 「사진 글」로 읽히지 않는다.
 */
function PostVideoMeta({
  post,
  showBadge,
  metas,
}: {
  post: unknown;
  showBadge: boolean;
  metas: ReturnType<typeof readVideoMetas>;
}) {
  return (
    <Stack spacing={1}>
      {showBadge && (
        <Box sx={{ position: 'relative', height: 24 }}>
          <PostMediaBadge post={post} hideImageCount videoCount={metas.length} />
        </Box>
      )}

      {metas.map((meta, index) => {
        // `grow` 는 «줄어들 수 있는 칸» 표시다 — 길어지는 것은 음악 제목뿐이라 그 칸만 준다
        const cells: { label: string; value: string; grow?: boolean }[] = [];
        const duration = formatVideoDuration(meta.duration);
        if (duration) cells.push({ label: '재생시간', value: duration });
        if (meta.music) cells.push({ label: '음악', value: meta.music, grow: true });
        if (meta.size !== null && meta.size !== undefined) {
          cells.push({ label: '크기', value: formatNumber(meta.size) });
        }
        // 「아니오」는 안 적는다 — 대부분이 거짓이라 적으면 잡음만 는다
        if (meta.isGenAi === true) cells.push({ label: 'AI 생성', value: '예' });

        return (
          <Stack
            // 메타에 식별자가 없어 순서로 잡는다 — 목록이 조회 중에 바뀌지 않는다
            key={`video-meta-${index}`}
            direction="row"
            spacing={2}
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: RADIUS.md,
              bgcolor: '#f9fafb',
              // 한 줄로 고정한다 — 음악 제목이 길어 줄이 늘면 상세가 통째로 밀린다.
              // 넘치는 값은 말줄임으로 끝낸다(값의 `noWrap`)
              flexWrap: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {metas.length > 1 && (
              <Typography variant="caption" sx={{ color: 'text.disabled', alignSelf: 'center', flexShrink: 0 }}>
                영상 {index + 1}
              </Typography>
            )}
            {cells.map((cell) => (
              <Stack
                key={cell.label}
                direction="row"
                spacing={1}
                sx={{ alignItems: 'baseline', minWidth: 0, flexShrink: cell.grow ? 1 : 0 }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                  {cell.label}
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, minWidth: 0 }}>
                  {cell.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        );
      })}
    </Stack>
  );
}

export default React.memo(PostDetail);